import React, { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  Award, Sparkles, Search, X, Plus, Paperclip, Link2,
  Trash2, Calendar, Users, Building2, ShieldCheck, ChevronDown, Info, Loader2,
  Trophy, PartyPopper, Rainbow, Star,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card } from '@/components/ui/card';
import { formatDate } from '../../lib/dateUtils';
import './rr-party.css';
import {
  bigCelebration, softConfettiDrizzle, sparklePoofAt, cardBurstFromButton,
  playDing,
  ACHIEVEMENTS, formatCountdown, TAGLINES, rotatingLabel,
} from './party';

const themeClass = (dept) => {
  const key = (dept || '').toLowerCase().replace(/\s+/g, '');
  const map = {
    strategy: 'rr-theme-strategy',
    postproduction: 'rr-theme-postproduction',
    production: 'rr-theme-production',
    design: 'rr-theme-design',
    accounts: 'rr-theme-accounts',
    hr: 'rr-theme-hr',
    agencywide: 'rr-theme-agencywide',
  };
  return map[key] || 'rr-theme-default';
};

/**
 * RR (Rewards & Recognition) nomination page.
 * Used by both employee and admin portals — the caller passes `api` (axios
 * instance with the right token) and `portal` ("employee" | "admin").
 *
 * Employees only ever see their OWN nominations. Admins/HR use a separate
 * console component (`AdminRRConsole`) for the full view.
 */
export default function RRNominatePage({ api, portal = 'employee' }) {
  const [loading, setLoading] = useState(true);
  const [edition, setEdition] = useState(null);
  const [categories, setCategories] = useState([]);
  const [clients, setClients] = useState([]);
  const [myNoms, setMyNoms] = useState([]);
  const [stats, setStats] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingNom, setEditingNom] = useState(null);
  const [tab, setTab] = useState('browse');
  const [celebrating, setCelebrating] = useState(null); // { nominee_name, suggested: [cats] }
  const [tick, setTick] = useState(0);
  const [tagIdx, setTagIdx] = useState(0);
  const [labelIdx, setLabelIdx] = useState(0);
  const enteredOnce = useRef(false);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [catRes, minRes, cliRes, statsRes] = await Promise.all([
        api.get('/api/rr/categories'),
        api.get('/api/rr/nominations/mine'),
        api.get('/api/rr/clients-lite').catch(() => ({ data: { clients: [] } })),
        api.get('/api/rr/live-stats').catch(() => ({ data: null })),
      ]);
      setCategories(catRes.data.categories || []);
      setEdition(catRes.data.edition || null);
      setMyNoms(minRes.data.nominations || []);
      setClients(cliRes.data.clients || []);
      setStats(statsRes.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load Rewards & Recognition');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  // Tick the countdown once a minute + rotate taglines / button labels
  useEffect(() => {
    const iv = setInterval(() => setTick(t => t + 1), 60000);
    const tagIv = setInterval(() => setTagIdx(i => (i + 1) % TAGLINES.length), 4500);
    const labelIv = setInterval(() => setLabelIdx(i => i + 1), 5000);
    return () => { clearInterval(iv); clearInterval(tagIv); clearInterval(labelIv); };
  }, []);

  // Once-per-session welcome drizzle
  useEffect(() => {
    if (!loading && !enteredOnce.current) {
      enteredOnce.current = true;
      const key = 'rr_welcome_drizzle_' + new Date().toDateString();
      if (!sessionStorage.getItem(key)) {
        sessionStorage.setItem(key, '1');
        setTimeout(() => softConfettiDrizzle(), 250);
      }
    }
  }, [loading]);

  const countdown = useMemo(() => formatCountdown(edition?.closes_at), [edition, tick]);

  const openNew = () => { setEditingNom(null); setShowForm(true); };
  const openEdit = (nom) => { setEditingNom(nom); setShowForm(true); };
  const openForCat = (cat, evt) => {
    if (evt?.currentTarget) cardBurstFromButton(evt.currentTarget);
    setEditingNom({ prefillAwardId: cat._id });
    setShowForm(true);
  };

  // ------------------------------------------------------------ achievements
  const achievementCtx = useMemo(() => {
    const myAwardSet = new Set(myNoms.map(n => n.award_id));
    const myNomineeSet = new Set();
    myNoms.forEach(n => (n.nominee_ids || []).forEach(x => myNomineeSet.add(x)));
    const agencyWideCount = myNoms.filter(n => (n.award_department || '').toLowerCase() === 'agency wide').length;
    const latest = myNoms.reduce((max, n) => {
      const t = n.created_at ? new Date(n.created_at).getTime() : 0;
      return t > max ? t : max;
    }, 0);
    return {
      myCount: myNoms.length,
      myAwardCount: myAwardSet.size,
      myNomineeCount: myNomineeSet.size,
      agencyWideCount,
      latestNominationAt: latest ? new Date(latest).toISOString() : null,
      closesAt: edition?.closes_at,
    };
  }, [myNoms, edition]);
  const unlockedIds = useMemo(() => ACHIEVEMENTS.filter(a => a.check(achievementCtx)).map(a => a.id), [achievementCtx]);

  const suggestedForCelebration = useMemo(() => {
    const touched = new Set(myNoms.map(n => n.award_id));
    return categories.filter(c => !touched.has(c._id) && c.eligible_count > 0).slice(0, 3);
  }, [categories, myNoms]);

  const handleSubmitted = (nom) => {
    setShowForm(false);
    bigCelebration();
    playDing();
    setCelebrating({
      nominee_name: nom?.nominees?.[0]?.name || 'them',
      award_name: nom?.award_name,
    });
    fetchAll();
  };

  const toggleSound = null; // kept intentionally minimal — sound is always on
  const togglePartyMode = null; // party mode is intentionally not user-configurable

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-[400px] rr-party-scope" data-testid="rr-loading">
        <Loader2 className="w-6 h-6 animate-spin text-amber-500" />
      </div>
    );
  }

  return (
    <div className="rr-party-scope" data-testid="rr-nominate-page">
      <div className="rr-rainbow-bar" />
      <div className="p-3 md:p-8 max-w-7xl">
        {/* Hero */}
        <div className="mb-4 md:mb-6 rounded-2xl md:rounded-3xl bg-gradient-to-br from-amber-50 via-rose-50 to-fuchsia-50 border border-amber-200/70 p-4 md:p-8 shadow-lg relative overflow-hidden">
          {/* Floating sparkles */}
          <Sparkles className="absolute top-4 right-8 w-5 h-5 text-yellow-400 animate-pulse hidden sm:block" />
          <PartyPopper className="absolute bottom-6 right-16 w-6 h-6 text-pink-400 opacity-70 hidden sm:block" />
          <Star className="absolute top-16 right-40 w-4 h-4 text-amber-400 opacity-60 animate-pulse hidden md:block" />

          <div className="flex flex-col md:flex-row md:flex-wrap md:items-start md:justify-between gap-3 md:gap-4 relative z-10">
            <div className="max-w-2xl">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/70 backdrop-blur-sm text-amber-800 text-[11px] md:text-xs font-semibold mb-2 md:mb-3 shadow-sm">
                <PartyPopper className="w-3.5 h-3.5" /> Rewards &amp; Recognition Night
              </div>
              <h1 className="text-xl md:text-4xl font-bold text-neutral-900 tracking-tight leading-tight">
                {edition?.name || 'HED R&R Night 2026'}
              </h1>
              <p className="text-sm md:text-base text-neutral-700 italic mt-2 transition-all duration-500" key={tagIdx}>
                {TAGLINES[tagIdx]}
              </p>
              <div className="mt-3 md:mt-4 flex flex-wrap gap-2 text-xs md:text-sm text-neutral-700">
                {edition?.event_date && (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/60" data-testid="rr-event-date">
                    <Calendar className="w-3.5 h-3.5 md:w-4 md:h-4 text-amber-600" />
                    {formatDate(edition.event_date)}
                  </span>
                )}
                {countdown && (
                  <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-white font-semibold ${countdown.expired ? 'bg-neutral-500' : countdown.urgent ? 'bg-red-500 animate-pulse' : 'bg-emerald-600'}`} data-testid="rr-countdown">
                    <ShieldCheck className="w-3.5 h-3.5 md:w-4 md:h-4" /> {countdown.text}
                  </span>
                )}
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/60">
                  <Users className="w-3.5 h-3.5 md:w-4 md:h-4 text-neutral-500" />
                  <span className="hidden sm:inline">{edition?.judged_by_label || 'Judged by Management and HR'}</span>
                  <span className="sm:hidden">{(edition?.judged_by_label || 'Mgmt + HR').replace('Judged by ', '')}</span>
                </span>
              </div>

              {/* Live stats */}
              {stats && (
                <div className="mt-3 md:mt-4 grid grid-cols-3 gap-2 text-xs">
                  <div className="bg-white/70 rounded-xl p-2 md:p-2.5 text-center">
                    <p className="text-[9px] md:text-[10px] text-neutral-500 uppercase tracking-wider">Org-wide</p>
                    <p className="text-base md:text-lg font-bold text-neutral-900">{stats.total_nominations}</p>
                  </div>
                  <div className="bg-white/70 rounded-xl p-2 md:p-2.5 text-center">
                    <p className="text-[9px] md:text-[10px] text-neutral-500 uppercase tracking-wider">Awards</p>
                    <p className="text-base md:text-lg font-bold text-neutral-900">{stats.unique_awards_touched}</p>
                  </div>
                  <div className="bg-white/70 rounded-xl p-2 md:p-2.5 text-center">
                    <p className="text-[9px] md:text-[10px] text-neutral-500 uppercase tracking-wider">Yours</p>
                    <p className="text-base md:text-lg font-bold text-amber-700">{stats.my_nominations}</p>
                  </div>
                </div>
              )}
            </div>
            <div className="w-full md:w-auto flex md:flex-col items-stretch md:items-end gap-2">
              <Button
                onClick={openNew}
                disabled={!edition?.is_open}
                className={`w-full md:w-auto bg-gradient-to-r from-amber-500 via-pink-500 to-fuchsia-500 hover:from-amber-600 hover:via-pink-600 hover:to-fuchsia-600 text-white shadow-lg text-sm md:text-base h-10 md:h-11 px-4 md:px-5 ${countdown?.urgent ? 'rr-pulse' : ''}`}
                data-testid="rr-new-nomination-btn"
              >
                <PartyPopper className="w-4 h-4 mr-1.5" /> {rotatingLabel(labelIdx)}
              </Button>
            </div>
          </div>
          {!edition?.is_open && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-xl flex items-start gap-2 text-sm text-red-800">
              <Info className="w-4 h-4 mt-0.5" />
              Nominations are currently closed. Reach out to HR if you need the window reopened.
            </div>
          )}
        </div>

        {/* Achievements */}
        <div className="mb-5">
          <div className="flex items-center gap-2 mb-2">
            <Trophy className="w-4 h-4 text-amber-500" />
            <h2 className="text-xs font-bold uppercase tracking-wider text-neutral-500">Your Nominator Badges</h2>
            <span className="text-xs text-neutral-400">· {unlockedIds.length} of {ACHIEVEMENTS.length} unlocked</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {ACHIEVEMENTS.map(a => {
              const on = unlockedIds.includes(a.id);
              return (
                <div
                  key={a.id}
                  className={`rr-badge inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold ${on ? 'bg-gradient-to-r from-amber-100 to-pink-100 text-amber-800 border border-amber-200 shadow-sm' : 'bg-neutral-100 text-neutral-400 border border-neutral-200 locked'}`}
                  title={a.hint}
                  data-testid={`rr-badge-${a.id}`}
                >
                  <span className="text-base">{a.emoji}</span>
                  {a.title}
                </div>
              );
            })}
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-5">
          <button
            onClick={() => setTab('browse')}
            className={`px-4 py-2 rounded-full text-sm font-semibold transition-all ${tab === 'browse' ? 'bg-gradient-to-r from-amber-500 to-pink-500 text-white shadow-md' : 'bg-white text-neutral-600 hover:bg-neutral-100 border border-neutral-200'}`}
            data-testid="rr-tab-browse"
          >
            Browse Awards
          </button>
          <button
            onClick={() => setTab('mine')}
            className={`px-4 py-2 rounded-full text-sm font-semibold transition-all ${tab === 'mine' ? 'bg-gradient-to-r from-amber-500 to-pink-500 text-white shadow-md' : 'bg-white text-neutral-600 hover:bg-neutral-100 border border-neutral-200'}`}
            data-testid="rr-tab-mine"
          >
            My Nominations · {myNoms.length}
          </button>
        </div>

        {tab === 'browse' && (
          <BrowseCategories categories={categories} onNominate={openForCat} isOpen={!!edition?.is_open} touchedIds={new Set(myNoms.map(n => n.award_id))} />
        )}

        {tab === 'mine' && (
          <MyNominationsList noms={myNoms} onEdit={openEdit} onRefresh={fetchAll} api={api} canEdit={!!edition?.is_open} onNewOne={openNew} />
        )}

        {showForm && (
          <NominationFormDialog
            api={api}
            edition={edition}
            categories={categories}
            clients={clients}
            initialNom={editingNom}
            onClose={() => setShowForm(false)}
            onSaved={handleSubmitted}
          />
        )}

        {celebrating && (
          <CelebrationModal
            info={celebrating}
            suggested={suggestedForCelebration}
            onClose={() => setCelebrating(null)}
            onPick={(cat) => { setCelebrating(null); openForCat(cat); }}
          />
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
function BrowseCategories({ categories, onNominate, isOpen, touchedIds }) {
  const [q, setQ] = useState('');
  const [deptFilter, setDeptFilter] = useState('all');

  const departments = useMemo(() => {
    const set = new Set(categories.map(c => c.department).filter(Boolean));
    return ['all', ...Array.from(set)];
  }, [categories]);

  const filtered = useMemo(() => {
    return categories.filter(c => {
      if (deptFilter !== 'all' && c.department !== deptFilter) return false;
      if (q) {
        const needle = q.toLowerCase();
        return (c.name || '').toLowerCase().includes(needle) ||
               (c.description || '').toLowerCase().includes(needle) ||
               (c.bucket || '').toLowerCase().includes(needle);
      }
      return true;
    });
  }, [categories, q, deptFilter]);

  const grouped = useMemo(() => {
    const g = {};
    filtered.forEach(c => {
      const d = c.department || 'Other';
      if (!g[d]) g[d] = [];
      g[d].push(c);
    });
    return g;
  }, [filtered]);

  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
          <Input
            placeholder="Search awards…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-9 bg-white"
            data-testid="rr-award-search"
          />
        </div>
        <div className="flex gap-1 flex-wrap">
          {departments.map(d => (
            <button
              key={d}
              onClick={() => setDeptFilter(d)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${
                deptFilter === d ? 'bg-gradient-to-r from-amber-500 to-pink-500 text-white shadow-md' : 'bg-white text-neutral-600 border border-neutral-200 hover:bg-neutral-50'
              }`}
              data-testid={`rr-dept-filter-${d}`}
            >
              {d === 'all' ? 'All departments' : d}
            </button>
          ))}
        </div>
      </div>

      {Object.keys(grouped).length === 0 && (
        <Card className="p-12 text-center text-neutral-500" data-testid="rr-no-awards">
          <div className="text-4xl mb-2">🌟</div>
          <p className="font-semibold text-neutral-700">Nothing found — but the perfect nominee is out there.</p>
          <p className="text-xs text-neutral-500 mt-1">Try a different search or department.</p>
        </Card>
      )}

      {Object.entries(grouped).map(([dept, list]) => (
        <div key={dept} className="mb-8">
          <div className="flex items-center gap-2 mb-3">
            {dept === 'Agency Wide' ? <Rainbow className="w-4 h-4 text-pink-400" /> : <Building2 className="w-4 h-4 text-neutral-400" />}
            <h2 className="text-sm font-bold uppercase tracking-wider text-neutral-500">{dept}</h2>
            <span className="text-xs text-neutral-400">· {list.length} award{list.length !== 1 ? 's' : ''}</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 rr-stagger">
            {list.map(c => {
              const alreadyTouched = touchedIds?.has(c._id);
              const isFun = c.department === 'Agency Wide';
              return (
                <Card
                  key={c._id}
                  className={`rr-award-card p-5 ${themeClass(c.department)} border-2 ${isFun ? 'border-pink-200' : 'border-white/50'}`}
                  data-testid={`rr-award-card-${c._id}`}
                >
                  <div className={`rr-ribbon ${isFun ? 'rainbow' : ''}`} />
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="flex-1">
                      <div className="text-[10px] font-bold text-neutral-800/80 uppercase tracking-wider mb-1 flex items-center gap-1">
                        {isFun && <span>🎊</span>}
                        {c.bucket}
                      </div>
                      <h3 className="text-base font-bold text-neutral-900 leading-tight">{c.name}</h3>
                    </div>
                    <Trophy className="rr-trophy w-6 h-6 text-amber-600 shrink-0 drop-shadow" />
                  </div>
                  <p className="text-xs text-neutral-700 leading-relaxed mb-3 line-clamp-3">{c.description}</p>
                  <div className="flex items-center justify-between pt-2 border-t border-white/50">
                    <div className="flex flex-col">
                      <span className="text-xs text-neutral-700 font-medium">
                        {c.department_restricted
                          ? `${c.eligible_count} eligible`
                          : `Open to all · ${c.eligible_count}`}
                      </span>
                      {alreadyTouched && <span className="text-[10px] text-emerald-700 font-semibold mt-0.5 inline-flex items-center gap-1"><Star className="w-3 h-3 fill-emerald-500 text-emerald-500" /> you&apos;ve filed one</span>}
                    </div>
                    <Button
                      size="sm"
                      onClick={(evt) => onNominate(c, evt)}
                      disabled={!isOpen || c.eligible_count === 0}
                      className="bg-white/80 hover:bg-white text-neutral-900 border border-neutral-900/10 shadow-sm h-8 text-xs font-semibold"
                      data-testid={`rr-nominate-btn-${c._id}`}
                    >
                      <PartyPopper className="w-3 h-3 mr-1" /> Nominate
                    </Button>
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
function MyNominationsList({ noms, onEdit, onRefresh, api, canEdit, onNewOne }) {
  const handleDelete = async (nom) => {
    if (!window.confirm(`Delete your nomination for "${nom.award_name}"?`)) return;
    try {
      await api.delete(`/api/rr/nominations/${nom._id}`);
      toast.success('Nomination deleted');
      onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    }
  };

  if (noms.length === 0) {
    return (
      <Card className="p-12 text-center rr-theme-agencywide" data-testid="rr-empty-mine">
        <div className="text-5xl mb-3">🥂</div>
        <h3 className="text-base font-bold text-neutral-800 mb-1">Nothing here yet.</h3>
        <p className="text-sm text-neutral-600 mb-4">Pop the champagne, then pop back to nominate someone.</p>
        {onNewOne && (
          <Button onClick={onNewOne} className="bg-gradient-to-r from-amber-500 to-pink-500 text-white" data-testid="rr-empty-cta">
            <PartyPopper className="w-4 h-4 mr-1.5" /> File your first nomination
          </Button>
        )}
      </Card>
    );
  }

  return (
    <div className="space-y-3 rr-stagger">
      {noms.map(nom => (
        <Card key={nom._id} className="p-5 bg-white border-l-4 border-amber-400" data-testid={`rr-my-nom-${nom._id}`}>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="flex-1 min-w-[240px]">
              <div className="flex flex-wrap items-center gap-2 mb-2">
                <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[11px] font-semibold uppercase tracking-wide">{nom.award_department}</span>
                <h3 className="text-base font-bold text-neutral-900">{nom.award_name}</h3>
              </div>
              <div className="flex flex-wrap items-center gap-1.5 mb-2">
                {nom.nominees.map(n => (
                  <span key={n.id} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gradient-to-r from-amber-100 to-pink-100 text-amber-800 text-xs font-semibold">
                    <Trophy className="w-3 h-3" /> {n.name}
                  </span>
                ))}
              </div>
              {nom.note && <p className="text-sm text-neutral-600 line-clamp-3 mb-2 italic">&ldquo;{nom.note}&rdquo;</p>}
              <div className="flex flex-wrap gap-2 text-xs text-neutral-500">
                {nom.urls?.length > 0 && (
                  <span className="inline-flex items-center gap-1"><Link2 className="w-3 h-3" />{nom.urls.length} link{nom.urls.length !== 1 ? 's' : ''}</span>
                )}
                {nom.attachments?.length > 0 && (
                  <span className="inline-flex items-center gap-1"><Paperclip className="w-3 h-3" />{nom.attachments.length} file{nom.attachments.length !== 1 ? 's' : ''}</span>
                )}
                {nom.client_name && (
                  <span className="inline-flex items-center gap-1"><Building2 className="w-3 h-3" />{nom.client_name}</span>
                )}
                <span>· submitted {formatDate(nom.created_at)}</span>
              </div>
            </div>
            <div className="flex gap-1.5">
              <Button size="sm" variant="outline" onClick={() => onEdit(nom)} disabled={!canEdit} data-testid={`rr-edit-btn-${nom._id}`}>Edit</Button>
              <Button size="sm" variant="outline" className="text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => handleDelete(nom)} disabled={!canEdit} data-testid={`rr-delete-btn-${nom._id}`}>
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>
        </Card>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Celebration modal — shown right after a successful submit
function CelebrationModal({ info, suggested, onClose, onPick }) {
  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[70] flex items-center justify-center p-4" onClick={onClose} data-testid="rr-celebration">
      <div
        className="rr-theme-agencywide rounded-3xl max-w-lg w-full shadow-2xl border-4 border-white/60 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-8 text-center">
          <div className="text-6xl mb-3 animate-bounce">🎊</div>
          <h2 className="text-2xl font-bold text-neutral-900 mb-2">Nomination locked in!</h2>
          <p className="text-neutral-700 mb-1">
            <span className="font-bold">{info?.nominee_name}</span> just got put forward for
          </p>
          <p className="text-lg font-semibold text-amber-800 mb-6">&ldquo;{info?.award_name}&rdquo;</p>

          {suggested.length > 0 ? (
            <>
              <p className="text-xs uppercase tracking-wider text-neutral-500 mb-3 font-semibold">Who else deserves it? Try one of these &rarr;</p>
              <div className="space-y-2 mb-5 text-left">
                {suggested.map(c => (
                  <button
                    key={c._id}
                    onClick={() => onPick(c)}
                    className="w-full p-3 bg-white/80 hover:bg-white rounded-2xl flex items-center gap-3 border border-white/60 transition-all hover:shadow-md hover:-translate-y-0.5"
                    data-testid={`rr-celebration-suggest-${c._id}`}
                  >
                    <Trophy className="w-5 h-5 text-amber-500 shrink-0" />
                    <div className="text-left flex-1">
                      <div className="text-[10px] uppercase tracking-wider text-neutral-500 font-bold">{c.department} &middot; {c.bucket}</div>
                      <div className="text-sm font-semibold text-neutral-900">{c.name}</div>
                    </div>
                    <PartyPopper className="w-4 h-4 text-pink-500" />
                  </button>
                ))}
              </div>
            </>
          ) : (
            <p className="text-sm text-neutral-600 mb-5">You&apos;ve touched every possible award &mdash; you legend.</p>
          )}
          <Button variant="outline" onClick={onClose} className="w-full bg-white" data-testid="rr-celebration-close">
            Done for now
          </Button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
function NominationFormDialog({ api, edition, categories, clients, initialNom, onClose, onSaved }) {
  const isEdit = initialNom && initialNom._id;
  const prefillId = initialNom?.prefillAwardId || initialNom?.award_id || '';
  const [awardId, setAwardId] = useState(prefillId);
  const [nomineeIds, setNomineeIds] = useState(initialNom?.nominee_ids || []);
  const [note, setNote] = useState(initialNom?.note || '');
  const [urls, setUrls] = useState(initialNom?.urls || []);
  const [urlDraft, setUrlDraft] = useState('');
  const [attachments, setAttachments] = useState(initialNom?.attachments || []);
  const [clientId, setClientId] = useState(initialNom?.client_id || '');
  const [empSearch, setEmpSearch] = useState('');
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const selectedCat = useMemo(
    () => categories.find(c => c._id === awardId),
    [categories, awardId]
  );

  // When award changes, wipe nominees that are no longer eligible
  useEffect(() => {
    if (!selectedCat) return;
    if (!selectedCat.department_restricted) return;
    const eligibleIds = new Set(selectedCat.eligible_employees.map(e => e._id));
    setNomineeIds(prev => prev.filter(id => eligibleIds.has(id)));
  }, [awardId]); // eslint-disable-line

  const filteredEmployees = useMemo(() => {
    if (!selectedCat) return [];
    const list = selectedCat.eligible_employees || [];
    if (!empSearch) return list;
    const n = empSearch.toLowerCase();
    return list.filter(e => (e.name || '').toLowerCase().includes(n));
  }, [selectedCat, empSearch]);

  const toggleNominee = (id) => {
    // STRICT 1-per-nomination: selecting a person replaces the previous pick.
    setNomineeIds(prev => (prev.includes(id) ? [] : [id]));
  };

  const addUrl = () => {
    const u = urlDraft.trim();
    if (!u) return;
    if (!/^https?:\/\//i.test(u)) {
      toast.error('URLs must start with http:// or https://');
      return;
    }
    if (urls.length >= 15) {
      toast.error('Max 15 links per nomination');
      return;
    }
    setUrls([...urls, u]);
    setUrlDraft('');
  };

  const onFilePick = async (e) => {
    const files = Array.from(e.target.files || []);
    if (attachments.length + files.length > 5) {
      toast.error('Max 5 files per nomination');
      return;
    }
    setUploading(true);
    try {
      for (const f of files) {
        if (f.size > 25 * 1024 * 1024) {
          toast.error(`${f.name} is over 25 MB`);
          continue;
        }
        const fd = new FormData();
        fd.append('file', f);
        const res = await api.post('/api/rr/attachments/upload', fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        setAttachments(prev => [...prev, res.data]);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const removeAttachment = (idx) => setAttachments(attachments.filter((_, i) => i !== idx));
  const removeUrl = (idx) => setUrls(urls.filter((_, i) => i !== idx));

  const handleSubmit = async () => {
    if (!awardId) return toast.error('Pick an award category');
    if (nomineeIds.length !== 1) return toast.error('Pick exactly one nominee (file another nomination for another person)');
    setSaving(true);
    try {
      const payload = {
        award_id: awardId,
        nominee_ids: nomineeIds,
        note,
        urls,
        attachments,
        client_id: clientId || null,
      };
      let created = null;
      if (isEdit) {
        const res = await api.patch(`/api/rr/nominations/${initialNom._id}`, payload);
        created = res.data;
        toast.success('Nomination updated');
      } else {
        const res = await api.post('/api/rr/nominations', payload);
        created = res.data;
      }
      onSaved(created);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-start justify-center overflow-y-auto p-2 md:p-4 py-4 md:py-8"
      onClick={onClose}
      data-testid="rr-form-backdrop"
    >
      <div
        className="bg-white rounded-2xl md:rounded-3xl max-w-3xl w-full shadow-2xl my-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="rr-nomination-form"
      >
        <div className="p-4 md:p-6 border-b border-neutral-100 flex items-center justify-between">
          <div>
            <h2 className="text-base md:text-lg font-bold text-neutral-900">{isEdit ? 'Edit Nomination' : 'New Nomination'}</h2>
            <p className="text-[11px] md:text-xs text-neutral-500 mt-0.5">Only you, HR and Admin can see this nomination.</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-neutral-100 rounded-full" data-testid="rr-form-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-4 md:p-6 space-y-4 md:space-y-5 max-h-[75vh] overflow-y-auto">
          {/* Category picker */}
          <div>
            <label className="text-xs font-semibold uppercase tracking-wide text-neutral-500 mb-1.5 block">Award category *</label>
            <div className="relative">
              <select
                value={awardId}
                onChange={(e) => setAwardId(e.target.value)}
                className="w-full h-10 px-3 pr-9 rounded-xl border border-neutral-200 bg-white text-sm appearance-none focus:outline-none focus:ring-2 focus:ring-amber-300"
                data-testid="rr-form-award-select"
              >
                <option value="">— select an award —</option>
                {categories.map(c => (
                  <option key={c._id} value={c._id}>{c.department} · {c.name}</option>
                ))}
              </select>
              <ChevronDown className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400" />
            </div>
            {selectedCat && (
              <p className="text-xs text-neutral-500 mt-1.5 italic">{selectedCat.description}</p>
            )}
          </div>

          {/* Nominee (strict: exactly one) */}
          {selectedCat && (
            <div>
              <label className="text-xs font-semibold uppercase tracking-wide text-neutral-500 mb-1.5 block">
                Nominee * · one person per nomination
              </label>
              <p className="text-[11px] text-neutral-500 mb-2">
                {selectedCat.department_restricted
                  ? `Choose one person from ${selectedCat.eligible_departments?.length ? selectedCat.eligible_departments.join(', ') : selectedCat.department}. Want to nominate more? File another nomination.`
                  : 'Choose any one employee. To nominate someone else for the same award, submit another nomination.'}
              </p>
              {nomineeIds.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {selectedCat.eligible_employees
                    .filter(e => nomineeIds.includes(e._id))
                    .map(e => (
                      <span key={e._id} className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-amber-100 text-amber-800 text-xs">
                        {e.name}
                        <button onClick={() => toggleNominee(e._id)} className="hover:text-amber-900" data-testid={`rr-remove-nominee-${e._id}`}>
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                </div>
              )}
              <Input
                placeholder="Search teammates…"
                value={empSearch}
                onChange={(e) => setEmpSearch(e.target.value)}
                className="mb-2"
                data-testid="rr-form-nominee-search"
              />
              <div className="border border-neutral-200 rounded-xl max-h-48 overflow-y-auto">
                {filteredEmployees.length === 0 ? (
                  <div className="p-4 text-center text-sm text-neutral-500">
                    {selectedCat.eligible_employees.length === 0
                      ? `No active employees in the ${selectedCat.department} department yet.`
                      : 'No matches for your search.'}
                  </div>
                ) : (
                  filteredEmployees.slice(0, 100).map(e => {
                    const selected = nomineeIds.includes(e._id);
                    return (
                      <div
                        key={e._id}
                        role="button"
                        tabIndex={0}
                        onClick={() => toggleNominee(e._id)}
                        onKeyDown={(evt) => {
                          if (evt.key === 'Enter' || evt.key === ' ') {
                            evt.preventDefault();
                            toggleNominee(e._id);
                          }
                        }}
                        className={`p-2.5 flex items-center gap-3 cursor-pointer border-b border-neutral-100 last:border-b-0 hover:bg-neutral-50 transition-colors focus:outline-none focus:bg-amber-50 ${selected ? 'bg-amber-50' : ''}`}
                        data-testid={`rr-form-emp-${e._id}`}
                      >
                        <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${selected ? 'border-amber-600' : 'border-neutral-300'}`}>
                          {selected && <div className="w-2 h-2 bg-amber-600 rounded-full" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-neutral-900 truncate">{e.name}</p>
                          <p className="text-xs text-neutral-500 truncate">{e.designation} · {(e.departments || []).join(', ')}</p>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          )}

          {/* Note */}
          <div>
            <label className="text-xs font-semibold uppercase tracking-wide text-neutral-500 mb-1.5 block">Why do they deserve it?</label>
            <Textarea
              placeholder="Give specifics — the campaign, the deadline they beat, the client they saved. The more detail, the stronger the case."
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={5}
              className="resize-none"
              data-testid="rr-form-note"
            />
            <p className="text-[11px] text-neutral-400 mt-1">{note.length} / 4000</p>
          </div>

          {/* URLs */}
          <div>
            <label className="text-xs font-semibold uppercase tracking-wide text-neutral-500 mb-1.5 block">
              Supporting links · videos, drives, campaign decks
            </label>
            <div className="flex gap-2">
              <Input
                placeholder="https://…"
                value={urlDraft}
                onChange={(e) => setUrlDraft(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addUrl(); } }}
                data-testid="rr-form-url-input"
              />
              <Button type="button" variant="outline" onClick={addUrl} data-testid="rr-form-url-add">
                <Plus className="w-4 h-4" />
              </Button>
            </div>
            {urls.length > 0 && (
              <div className="mt-2 space-y-1.5">
                {urls.map((u, idx) => (
                  <div key={idx} className="flex items-center gap-2 p-2 bg-neutral-50 rounded-lg text-xs">
                    <Link2 className="w-3 h-3 text-neutral-400 shrink-0" />
                    <a href={u} target="_blank" rel="noopener noreferrer" className="flex-1 truncate text-blue-600 hover:underline">{u}</a>
                    <button onClick={() => removeUrl(idx)} className="text-neutral-400 hover:text-red-600" data-testid={`rr-form-url-remove-${idx}`}>
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Attachments */}
          <div>
            <label className="text-xs font-semibold uppercase tracking-wide text-neutral-500 mb-1.5 block">
              Attachments · images & PDFs only (max 5 · 25 MB each)
            </label>
            <label className="flex items-center justify-center gap-2 h-20 border-2 border-dashed border-neutral-200 rounded-xl cursor-pointer hover:border-amber-300 hover:bg-amber-50/50 transition-colors" data-testid="rr-form-file-drop">
              {uploading ? (
                <Loader2 className="w-4 h-4 animate-spin text-amber-600" />
              ) : (
                <>
                  <Paperclip className="w-4 h-4 text-neutral-400" />
                  <span className="text-sm text-neutral-600">Click to attach files</span>
                </>
              )}
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif,application/pdf"
                multiple
                className="hidden"
                onChange={onFilePick}
                disabled={uploading || attachments.length >= 5}
                data-testid="rr-form-file-input"
              />
            </label>
            {attachments.length > 0 && (
              <div className="mt-2 space-y-1.5">
                {attachments.map((a, idx) => (
                  <div key={idx} className="flex items-center gap-2 p-2 bg-neutral-50 rounded-lg text-xs">
                    <Paperclip className="w-3 h-3 text-neutral-400 shrink-0" />
                    <a href={a.url} target="_blank" rel="noopener noreferrer" className="flex-1 truncate text-blue-600 hover:underline">{a.filename}</a>
                    <span className="text-neutral-400">{Math.round((a.size || 0) / 1024)} KB</span>
                    <button onClick={() => removeAttachment(idx)} className="text-neutral-400 hover:text-red-600" data-testid={`rr-form-att-remove-${idx}`}>
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Client */}
          {clients.length > 0 && (
            <div>
              <label className="text-xs font-semibold uppercase tracking-wide text-neutral-500 mb-1.5 block">Related client · optional</label>
              <div className="relative">
                <select
                  value={clientId || ''}
                  onChange={(e) => setClientId(e.target.value)}
                  className="w-full h-10 px-3 pr-9 rounded-xl border border-neutral-200 bg-white text-sm appearance-none focus:outline-none focus:ring-2 focus:ring-amber-300"
                  data-testid="rr-form-client-select"
                >
                  <option value="">— none —</option>
                  {clients.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
                </select>
                <ChevronDown className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400" />
              </div>
            </div>
          )}
        </div>

        <div className="p-4 md:p-6 border-t border-neutral-100 bg-neutral-50/50 rounded-b-2xl md:rounded-b-3xl flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <p className="text-[11px] md:text-xs text-neutral-500 order-2 sm:order-1">{edition?.judged_by_label || 'Judged by Management and HR'}</p>
          <div className="flex gap-2 order-1 sm:order-2">
            <Button variant="outline" onClick={onClose} data-testid="rr-form-cancel" className="flex-1 sm:flex-none">Cancel</Button>
            <Button
              onClick={handleSubmit}
              disabled={saving || !awardId || nomineeIds.length === 0}
              className="flex-1 sm:flex-none bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="rr-form-submit"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (isEdit ? 'Save Changes' : 'Submit Nomination · one person')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
